import React from 'react';
import { injectIntl, WrappedComponentProps } from '@kyruus/intl';
import { Typography } from '@biome-design-system/ui/Typography';
import { useLocation } from 'react-router-dom';
import { PMCConfig } from '@kyruus/types';

import {
  Disclaimer,
  Pagination,
  PoweredByLogo,
  SuggestionsContainer
} from '../styles';
import { Suggestions } from '../Suggestions';
import { isSearchRefined } from 'Src/utils/search-common';
import { useReferralCrmConnector } from '../../referral-crm-connector';
import {
  type GetUpdatedSearch,
  type Log,
  type SearchSummary,
  type SearchSuggestion
} from 'Src/types';
import { MODULES, isModuleEnabled } from 'Common/config';
import { messages } from './messages';
import { Footer } from './styles';

export interface BaseSearchFooterProps {
  searchSummary: SearchSummary;
  showAvailabilityDisclaimer: boolean;
  totalPages: number;
  getUpdatedSearch: GetUpdatedSearch;
  config: PMCConfig;
  log: Log;
  suggestions?: SearchSuggestion[];
}

export type SearchFooterProps = BaseSearchFooterProps & WrappedComponentProps;

export const SearchFooter = injectIntl(
  ({
    searchSummary,
    showAvailabilityDisclaimer,
    totalPages,
    getUpdatedSearch,
    config,
    log,
    intl,
    suggestions
  }: SearchFooterProps) => {
    const location = useLocation();
    const { connector } = useReferralCrmConnector(config);
    const showPagination = totalPages > 1;
    /**
     * KENG-28970
     * Adding some extra padding to the footer when crm is enabled
     * and referral list drawer is open
     */
    const hasReferrals = Boolean(
      isModuleEnabled(config, MODULES.REFERRAL) && connector.hasReferrals()
    );
    const showRefinedSearchDisclaimer = isSearchRefined(location.search);
    // Show disclaimers if either the availability disclaimer flag is true
    // or if the search is refined
    const showDisclaimers =
      showAvailabilityDisclaimer || showRefinedSearchDisclaimer;
    const showSuggestions =
      config?.KENG_38276_show_search_suggestions_v9 !== false &&
      suggestions &&
      suggestions.length > 0;

    return (
      <Footer
        id="search-footer"
        hasReferrals={hasReferrals}
        showPagination={showPagination}
        showDisclaimers={showDisclaimers}
      >
        {showPagination && (
          <Pagination
            searchSummary={searchSummary}
            totalPages={totalPages}
            getUpdatedSearch={getUpdatedSearch}
            log={log}
          />
        )}
        <PoweredByLogo config={config} />
        {showSuggestions ? (
          <SuggestionsContainer>
            <Suggestions
              getUpdatedSearch={getUpdatedSearch}
              log={log}
              suggestions={suggestions}
            />
          </SuggestionsContainer>
        ) : null}

        {showDisclaimers && (
          <Disclaimer data-testid="search-disclaimers">
            {showRefinedSearchDisclaimer && (
              <Typography
                component="p"
                color="text.secondary"
                data-testid="psr-search-disclaimer"
                variant="subtitle2"
              >{`*${intl.formatMessage(messages.psrDisclaimer)}`}</Typography>
            )}
            {showAvailabilityDisclaimer && (
              <Typography
                component="p"
                color="text.secondary"
                data-testid="availability-disclaimer"
                variant="subtitle2"
              >{`*${intl.formatMessage(messages.availabilityDisclaimer)}`}</Typography>
            )}
          </Disclaimer>
        )}
      </Footer>
    );
  }
);
